/**
 * Controlled blog category vocabulary.
 *
 * The `id` values are the URL slugs used at /blog/category/:id and MUST stay in
 * sync with the `blog_posts_category_check` constraint in
 * supabase/migrations/20260827120000_blog_system.sql. Adding a category is a
 * two-line change here plus one line in that CHECK constraint (new migration).
 *
 * Keep this list small and meaningful. Thin, near-empty categories are an SEO
 * liability, so a category only earns a place in the sitemap once it holds
 * enough published posts to be a useful landing page (see
 * BLOG_CATEGORY_SITEMAP_MIN_POSTS).
 */

export interface BlogCategory {
  /** URL slug. Stable. */
  id: string;
  /** Nav / chip label. */
  label: string;
  /** <h1> + <title> stem for the category archive. */
  title: string;
  /** Meta description + on-page intro for the category archive. */
  description: string;
}

export const BLOG_CATEGORIES: readonly BlogCategory[] = [
  {
    id: "shopify",
    label: "Shopify",
    title: "Shopify news and analysis",
    description:
      "Platform changes, release notes and what they mean in practice for established Shopify and Shopify Plus stores.",
  },
  {
    id: "shopify-engineering",
    label: "Shopify Engineering",
    title: "Shopify engineering notes",
    description:
      "Implementation notes from theme, Liquid, Hydrogen, Functions and checkout work, including the workflows and trade-offs behind them.",
  },
  {
    id: "seo",
    label: "SEO",
    title: "Technical SEO for Shopify",
    description:
      "Crawling, indexation, canonicalisation and structured data findings from real Shopify stores.",
  },
  {
    id: "performance",
    label: "Performance",
    title: "Shopify performance research",
    description:
      "Core Web Vitals experiments, render-path analysis and app-contention measurements on production Shopify themes.",
  },
  {
    id: "migrations",
    label: "Migrations",
    title: "Shopify migration learnings",
    description:
      "Lessons from replatforming and catalog migrations onto Shopify: data modelling, redirects, cutover sequencing and recovery.",
  },
  {
    id: "ai-automation",
    label: "AI & Automation",
    title: "AI and automation for commerce",
    description:
      "How AI assistants, agentic storefronts and automation change how DTC stores are built, measured and found.",
  },
  {
    id: "ecommerce",
    label: "Ecommerce",
    title: "Ecommerce strategy and operations",
    description:
      "Merchandising, conversion, retention and operational patterns we see across DTC brands.",
  },
  {
    id: "agency-insights",
    label: "Agency Insights",
    title: "Agency insights",
    description:
      "How we scope, run and validate engineering work, plus opinions and thought leadership from the team.",
  },
] as const;

export type BlogCategoryId = (typeof BLOG_CATEGORIES)[number]["id"];

export const BLOG_CATEGORY_IDS: readonly string[] = BLOG_CATEGORIES.map((c) => c.id);

/** A category needs at least this many published posts to enter the sitemap. */
export const BLOG_CATEGORY_SITEMAP_MIN_POSTS = 3;

export function getBlogCategory(id: string | null | undefined): BlogCategory | null {
  if (!id) return null;
  return BLOG_CATEGORIES.find((c) => c.id === id) ?? null;
}

export function blogCategoryLabel(id: string | null | undefined): string {
  return getBlogCategory(id)?.label ?? "Insights";
}
